import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@atlaskit/button';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import { useLazyQuery, useQuery } from '@apollo/client';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { FetchEntitlementsForCartQueryType, FetchEntitlementsForCartQueryVariables } from '@css-xp/ppc-cart-common';
import { CCP_SEARCH_MAX_LIMIT, maxThresholdForResults, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { CloudSiteContainer } from './components/cloud-sites-container';
import {
  BillingProfileOption,
  ExternalProductListFetchStatus,
  EntitlementListWithEmailFetchStatus,
  ExternalProductsList,
  SelectType,
  CloudSiteOrOrgNameMap,
  CloudSiteOrOrgNameMapGroupedByBillingProfile,
} from './types';
import { ValueType } from '@atlaskit/select';
import {
  NotificationTypes,
  WarningDialog,
  errorNotificationBuilder,
  useFeatureToggle,
  useNotification,
  successNotificationBuilder,
} from '@css-xp/base';
import { fetchBillingProfilesForCustomer } from '../../../queries/fetch-billing-profiles-for-customer';
import {
  useOfferingsAndPricingPlansStore,
  useStore,
  useTransferCloudSiteOrOrgNameMap,
  useExternalCloudSiteOrOrgNameMap,
  useCloudSiteOrOrgNameMapStore,
} from '../../../store';
import { getDataForStore } from './store-utils';
import { fetchEntitlementsForCart } from '../../../queries/fetch-entitlements';
import { OperationType } from '../../../types';
import { truthy } from '../../../utils';
import { SearchAndFilterContainer } from './components/search-and-filter/search-and-filter-container';
import { fetchCloudSiteProductsBelongingToOtherTxa } from './api';
import { fetchEntitlementsForEntityWithContactEmails, processEntity } from '../../entitlement-transfer/src/utils/api';
import { commonEntitlementQueryFilters } from './common-entitlement-query-filters';
import { NoEntitlement } from './components/no-entitlement';
import { useLoadEntitlements } from './hooks/use-load-entitlements';
import { Currency } from '@css-xp/ppc-common';

interface Props {
  customerIds: string[] | null;
  isSummaryDrawerOpen?: boolean;
}

interface User {
  email: string;
  error?: string | undefined;
}

export const transformEntitlement = (entitlementList: any[]) => {
  // sort entitlement to keep all the data in same order
  entitlementList.sort((a, b) => a.productName - b.productName);

  function mergeByCloudSiteName(
    cloudSiteMap: Record<string, { products: any[]; isChecked: boolean; inactiveProducts: [] }>,
    currentEntitlement: any
  ) {
    const { cloudSiteHostname } = currentEntitlement;
    cloudSiteMap[cloudSiteHostname] = cloudSiteMap[cloudSiteHostname] || {
      products: [],
      isChecked: false,
      inactiveProducts: [],
    };
    cloudSiteMap[cloudSiteHostname].products.push(currentEntitlement);
    return cloudSiteMap;
  }

  // create a map of cloud site name in order to use it for inner component
  return entitlementList.reduce(mergeByCloudSiteName, {});
};

const someEntitlementIsUnprovisionedErrorMsg =
  'Some of the subscriptions linked to the search criteria are not provisioned and are excluded from the search result';

const allEntitlementsAreUnprovisionedErrorMsg =
  'Subscriptions linked to the search criteria are not provisioned and are excluded from the search result';

export const BillingProfileWrapper = ({ customerIds, isSummaryDrawerOpen }: Props) => {
  const offeringsAndPricingPlans = useOfferingsAndPricingPlansStore();
  const [isEnabled] = useFeatureToggle();
  const isEntitlementFetchInChunksEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ENTITLEMENT_FETCH_IN_CHUNKS_ON_UOF);
  const isFetchBillingProfilesViaCCPEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_FROM_CCP);
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);

  const [
    {
      products,
      selectedProducts,
      invoiceGroupId,
      billingProfiles: billingProfilesFromStore,
      sidebarFilter,
      featureFlags,
    },
  ] = useStore();
  const { searchQuery, billingProfileIds } = sidebarFilter ?? {};

  const [cloudSiteOrOrgNameMapData, actions] = useCloudSiteOrOrgNameMapStore();
  const [cloudSiteOrOrgNameMapForTransfer] = useTransferCloudSiteOrOrgNameMap();
  const externalCloudSiteOrOrgNameMap = useExternalCloudSiteOrOrgNameMap();

  const { cloudSiteOrOrgNameMap, showTransferForm } = cloudSiteOrOrgNameMapData ?? {};
  const { txaId } = useParams<{ txaId: string }>();
  const [billProfileId, setBillProfileId] = useState('');
  const [billProfileLabel, setBillProfileLabel] = useState('');
  const [showTransferSearchResults, setShowTransferSearchResults] = useState(false);
  const [hasNoTransferSearchResults, setHasNoTransferSearchResults] = useState(false);
  const [billingProfileOption, setBillingProfileOption] = useState<ValueType<BillingProfileOption>>();
  const [isBillingProfileChangeConfirmOpen, setIsBillingProfileChangeConfirmOpen] = useState(false);
  const [showClearingCartWarning, setShowClearingCartWarning] = useState(false);
  const [transferExternalProductsList, setTransferExternalProductsList] = useState<ExternalProductsList>([]);
  const [externalProductsList, setExternalProductsList] = useState<ExternalProductsList>([]);
  const [externalProductListFetchStatus, setExternalProductListFetchStatus] = useState<ExternalProductListFetchStatus>(
    ExternalProductListFetchStatus.IDLE
  );
  const [
    entitlementListWithEmailFetchStatus,
    setEntitlementListWithEmailFetchStatus,
  ] = useState<EntitlementListWithEmailFetchStatus>(EntitlementListWithEmailFetchStatus.IDLE);
  const [isSubmittingTransferSearchForm, setIsSubmittingTransferSearchForm] = useState(false);
  const [emailList, setEmailList] = useState<Map<string, User>>(new Map());

  const isExternalProductListLoading = externalProductListFetchStatus === ExternalProductListFetchStatus.PENDING;

  const isEntitlementListWithEmailLoading =
    entitlementListWithEmailFetchStatus === EntitlementListWithEmailFetchStatus.PENDING;

  const shouldSkipBillingProfileFetch = !txaId || !customerIds || !customerIds.length;

  const showInactiveProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_INACTIVE_PRODUCTS_IN_UOF] || false;
  const shouldShowAddons = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS] || false;
  const showSandboxProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS] || false;
  const isSandboxDecouplingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING] || false;
  const isNonCommercialPricingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING] || false;
  const isMultiCurrencyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES] || false;
  const isJPDAsJiraFamilyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY] || false;
  const isFutureDatedEntitlementEnabled =
    featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS] || false;

  const isTWCPhase1Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1] || false;
  const isTWCPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2] || false;
  const isAddOnsPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2] || false;
  const isMultiInstanceAppsEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS] || false;

  const {
    data: billingProfileData,
    loading: isBillingProfileLoading,
    error: billingProfileError,
    refetch: refetchBillingProfileData,
  } = useQuery<
    generatedTypesNew.FetchBillingProfilesForCustomerQuery,
    generatedTypesNew.FetchBillingProfilesForCustomerQueryVariables
  >(fetchBillingProfilesForCustomer, {
    variables: {
      id: txaId,
      searchOptions: {
        first: isFetchBillingProfilesViaCCPEnabled ? CCP_SEARCH_MAX_LIMIT : maxThresholdForResults,
        offset: 0,
      },
      filterOptions: [
        {
          key: 'or',
          value: JSON.stringify(customerIds?.map((customerId) => ({ shipToId: customerId }))),
        },
      ],
      isFetchBillingProfilesViaCCPEnabled,
    },
    skip: shouldSkipBillingProfileFetch,
    // TODO: check why keeping `cache-and-network` policy causes billing profile refetch
    // fetchPolicy: 'cache-and-network',
  });

  const billingProfilesForCustomer = isFetchBillingProfilesViaCCPEnabled
    ? billingProfileData?.transactionAccount?.billingProfilesFromCCP?.nodes
    : billingProfileData?.transactionAccount?.billingProfiles?.nodes;

  const billingProfileResponseData = useMemo(() => {
    //TODO: Remove this hardcoded filter for deferred payment, done as a hotFix
    let filteredBillingProfile =
      billingProfilesForCustomer
        ?.map((billingProfile) => {
          return {
            ...billingProfile,
            // adding a transformer to update the pricing and currency to default values in case not available from backend.
            shipToPriceEligibility: billingProfile?.shipToPriceEligibility?.toUpperCase() ?? CCPPricing.COMMERCIAL,
            currency: billingProfile?.currency?.toUpperCase() ?? Currency.USD,
          };
        })
        .filter((billingProfile: any) =>
          showInactiveProducts ? true : billingProfile?.defaultPaymentMethod?.type !== 'DEFERRED'
        )
        .filter(truthy) ?? [];

    const selectedBillingProfile = filteredBillingProfile.find((profile: any) => profile.id === invoiceGroupId);

    if (isNonCommercialPricingEnabled) {
      filteredBillingProfile = filteredBillingProfile.filter(
        (billingProfile) =>
          billingProfile?.shipToPriceEligibility.toUpperCase() ===
          selectedBillingProfile?.shipToPriceEligibility?.toUpperCase()
      );
    }

    if (isMultiCurrencyEnabled) {
      filteredBillingProfile = filteredBillingProfile.filter(
        (billingProfile: any) =>
          billingProfile.currency.toUpperCase() === selectedBillingProfile?.currency?.toUpperCase()
      );
    }

    //filter active billing profiles
    return filteredBillingProfile?.filter((billingProfile) => billingProfile?.isActive === true);

    // return billingProfileData?.transactionAccount?.billingProfiles?.nodes?.filter?.(truthy) ?? [];
  }, [billingProfilesForCustomer, invoiceGroupId]);

  //@ts-ignore
  const billingProfiles = billingProfileResponseData
    .map(({ id, name }) => ({
      label: name ?? '',
      value: id ?? '',
    }))
    ?.sort((a, b) => (a.label < b.label ? -1 : 1));

  const invoiceGroupIdsForFilter = useMemo(() => {
    return billingProfileResponseData.map(({ id }) => id).filter(truthy);
  }, [billingProfileResponseData]);

  // fetch the existing subscriptions for a destination billing profile ids
  const {
    loading: isFetchEntitlementsLoading,
    error: fetchEntitlementsError,
    data: fetchEntitlementsResponse,
    refetch: refetchEntitlementsResponse,
  } = useLoadEntitlements(
    {
      id: txaId,
      filterOptions: [
        {
          key: 'invoiceGroupIdIsIn',
          values: invoiceGroupIdsForFilter,
        },
        ...(commonEntitlementQueryFilters(true, showInactiveProducts, shouldShowAddons) || []),
      ],
      searchOptions: {
        first: maxThresholdForResults, // Disclaimer: Max threshold for the query
        offset: 0,
      },
      isRestricted: false,
      fetchECCFromIdentity: false,
    },
    { isChunkEnabled: isEntitlementFetchInChunksEnabled, skip: !invoiceGroupIdsForFilter.length }
  );

  const [fetchEntitlement, { loading: isCloudSiteContainerLoading, error, data: entitlementsResponse }] = useLazyQuery<
    FetchEntitlementsForCartQueryType,
    FetchEntitlementsForCartQueryVariables
  >(fetchEntitlementsForCart);

  useEffect(() => {
    // return the cleanup method for cloud site map to be reset
    // 1. on unmount of the component
    return () => {
      actions.resetStore();
      clearSearchQuery();
    };
  }, [actions, customerIds]);

  useEffect(() => {
    if (entitlementsResponse) {
      const selectedBillingProfile = billingProfilesForCustomer?.find((profile: any) => profile.id === billProfileId);
      const productSubscriptions = entitlementsResponse?.transactionAccount?.productSubscriptions;
      const productsAndAppData = getDataForStore({
        entitlementSubscriptionsWrapper: productSubscriptions,
        offeringsAndpricingPlans: offeringsAndPricingPlans,
        operationType: OperationType.OWN,
        hasAllData: true,
        includeOrgProducts: true,
        showSandboxProducts,
        isSandboxDecouplingEnabled,
        isFutureDatedEntitlementEnabled,
        isNonCommercialPricingEnabled,
        destinationPricingEligibility: selectedBillingProfile?.shipToPriceEligibility || '',
        isTWCPhase1Enabled,
        isTWCPhase2Enabled,
        isAddOnsPhase2Enabled,
        isMultiInstanceAppsEnabled,
      });
      if (productsAndAppData) {
        actions.initializeStore(productsAndAppData);
        actions.setInvoiceGroup(billProfileId);
        actions.setManualInvoiceGroupId(billProfileId);
        actions.setInvoiceGroupName(billProfileLabel);
      }
    }
  }, [actions, billProfileId, billProfileLabel, entitlementsResponse]);

  useEffect(() => {
    if (!billingProfileResponseData.length) return;

    const billingProfilesForStore = billingProfileResponseData
      .map(({ id, name, shipToPriceEligibility, currency }) => {
        return {
          id: id ?? '',
          name: name ?? '',
          shipToPriceEligibility: shipToPriceEligibility as CCPPricing,
          currency: currency as `${generatedTypesNew.Currency}`,
        };
      })
      ?.sort((a, b) => (a.name < b.name ? -1 : 1));

    actions.setBillingProfiles(billingProfilesForStore);
  }, [actions, billingProfileResponseData]);

  useEffect(() => {
    if (fetchEntitlementsResponse) {
      const productSubscriptions = fetchEntitlementsResponse?.transactionAccount?.productSubscriptions;
      const selectedBillingProfile = billingProfilesForCustomer?.find((profile: any) => profile.id === invoiceGroupId);
      const hasAllData = false;
      const productsAndAppData = getDataForStore({
        entitlementSubscriptionsWrapper: productSubscriptions,
        offeringsAndpricingPlans: offeringsAndPricingPlans,
        operationType: OperationType.OWN,
        hasAllData,
        includeOrgProducts: true,
        showSandboxProducts,
        isSandboxDecouplingEnabled,
        isFutureDatedEntitlementEnabled,
        isNonCommercialPricingEnabled,
        destinationPricingEligibility: selectedBillingProfile?.shipToPriceEligibility || '',
        isTWCPhase1Enabled,
        isTWCPhase2Enabled,
        isAddOnsPhase2Enabled,
        isMultiInstanceAppsEnabled,
      });

      if (productsAndAppData) {
        actions.addProductsAndApps(productsAndAppData);

        (async function fetchExternalProductList() {
          try {
            if (isDesignIteration1Enabled) {
              return;
            }
            const cloudSiteIds = Object.values(productsAndAppData.products).map(({ cloudSiteId }) => cloudSiteId);
            if (cloudSiteIds && cloudSiteIds.length) {
              setExternalProductListFetchStatus(ExternalProductListFetchStatus.PENDING);
              const externalProductListResponse = await fetchCloudSiteProductsBelongingToOtherTxa({
                currentTransactionAccountId: txaId,
                cloudSiteIds,
              });
              setExternalProductsList(externalProductListResponse);
              setExternalProductListFetchStatus(ExternalProductListFetchStatus.SUCCESS);
            }
          } catch (e) {
            setExternalProductListFetchStatus(ExternalProductListFetchStatus.ERROR);
          }
        })();
      }
    }
  }, [actions, entitlementsResponse, fetchEntitlementsResponse, offeringsAndPricingPlans, txaId]);

  useEffect(() => {
    if (isSummaryDrawerOpen) {
      clearSearchQuery();
    }
  }, [isSummaryDrawerOpen]);

  const { notificationSent, sendSuccessNotification, setNotificationSent } = useNotification();

  const clearSearchQuery = () => {
    actions.setSearchQuery('');
    actions.setSidebarFilter({ billingProfileIds: [] });
    setShowTransferSearchResults(false);
    // resetting no transfer search results
    setHasNoTransferSearchResults(false);
    // Resetting the email field when the search query is cleared
    setEmailList(new Map());
  };

  const handleRefetchEntitlements = () => {
    refetchBillingProfileData().then(() => {
      refetchEntitlementsResponse();
    });
  };

  const handleConfirm = (billingProfileOption: ValueType<BillingProfileOption, false>) => {
    const totalSelectedProducts = Object.keys(selectedProducts ?? {});
    if (totalSelectedProducts.length > 0) {
      setBillingProfileOption(billingProfileOption);
      setIsBillingProfileChangeConfirmOpen(true);
      return;
    }
    onBillingProfileChange(billingProfileOption);
  };

  const onBillingProfileChange = (billingProfileOption: ValueType<BillingProfileOption, false>) => {
    actions.resetStore();

    const billingProfileId = billingProfileOption?.value;
    setBillProfileLabel(billingProfileOption?.label ?? '');
    setBillProfileId(billingProfileId ?? '');
    if (billingProfileId) {
      // entitlement has to be active
      fetchEntitlement({
        variables: {
          id: txaId,
          filterOptions: [
            {
              key: 'invoiceGroupId',
              value: billingProfileId,
            },
            ...(commonEntitlementQueryFilters(true, showInactiveProducts, shouldShowAddons) || []),
          ],
          searchOptions: {
            first: maxThresholdForResults, // Disclaimer: Max threshold for the query
            offset: 0,
          },
          isRestricted: false,
          fetchECCFromIdentity: false,
        },
      });
    }
  };

  const handleCheckedItems = (type: SelectType, identifier: string) => {
    if (!type || !identifier) return;
    actions.toggleSelectedItem(identifier, type, true);
  };

  const showCloudSiteContainer = (() => {
    // we want to show sidebar only when user has selected a customer. post which sidebar will decide what to show
    return customerIds?.length;
  })();
  const triggerErrorNotificationForTransfer = (message = '') => {
    const notificationOptions = errorNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('transfer-error-notification')
      .withHeader("We can't find what you're looking for")
      .withMessage(message)
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    setIsSubmittingTransferSearchForm(false);
  };
  const triggerErrorNotification = (message = '', header = '') => {
    if (notificationSent) {
      return;
    }
    const notificationOptions = errorNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('transfer-error-notification')
      .withHeader(header)
      .withMessage(message)
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  };

  const isSearchedEntitlementOrCloudsiteInCart = ({
    entitlementNumber,
    siteURL,
  }: {
    entitlementNumber?: string;
    siteURL?: string;
  }) => {
    /**
     * This function checks if the searched entitlement number or cloudsite url is present in the cart.
     */
    if (!entitlementNumber && !siteURL) return false;
    if (entitlementNumber) {
      const productId = Object.values(products)?.find((product) => product?.entitlementNumber === entitlementNumber)
        ?.id;
      if (productId) {
        return selectedProducts[productId];
      }
    }
    if (siteURL) {
      const productsGroupedByCloudSite = groupBy(Object.values(products), 'cloudSite');
      const cloudsiteProductIds = productsGroupedByCloudSite[siteURL]?.map((product) => product.id);
      if (cloudsiteProductIds && cloudsiteProductIds.length > 0) {
        return cloudsiteProductIds.some((productId) => selectedProducts[productId]);
      }
    }
    return false;
  };

  const handleTransferFormSubmit = async (data: { [key: string]: string }, currentCloudSiteHostname?: string) => {
    /**
     * This function handles the form submission for transfer search.
     *
     * 1. On initial transfer search
     * Method onInitialTransferSearchFormSubmit is called, which checks if the searched entitlement or cloudsite is already present in the cart.
     * If it is present, it opens a modal to inform the user about removal of the searched entity from the cart.
     * If it is not present OR on confirm from modal, it calls handleTransferFormSubmit to fetch the transfer entitlements and cloudsite products.
     *
     * 2. During partial transfer search
     * Method handleTransferFormSubmit is directly called, which fetches the transfer entitlements and cloudsite products.
     *
     * If we perform partial search on existing cloudsite where few entitlements are not transferred,
     * we are not removing the existing entitlements from the cart.
     * If we perform partial search on cloudsite which is not present in the cart,
     * we are removing the existing entitlements from the cart.
     *
     */

    let { ...emailObj } = data;

    /**
     * emailList stores the email in SearchAndFilterContainer, i.e the initial transfer search form.
     * emailObj stores the email in AddCustomerEmailField, i.e the partial transfer search form.
     *
     * During partial search on previously searched cloudsite, we need to merge the email from emailList and emailObj.
     * During partial search of existing cloudsite, since emailList will be empty, we skip merging and take emailObj as it is.
     *
     */
    if (emailList && emailList.size > 0) {
      const existingEmailObj: Record<string, string> = {};
      emailList.forEach(({ email }, key) => {
        // If the email is not present in emailObj, then only we add it to emailObj.
        if (!Object.values(emailObj).includes(email)) {
          existingEmailObj[key] = email;
        }
      });
      emailObj = { ...emailObj, ...existingEmailObj };

      // Resetting the emailList after merging the email from emailList and emailObj.
      const updatedEmailList = new Map<string, User>();
      Object.entries(emailObj).forEach(([key, email]) => {
        const user: User = { email };
        updatedEmailList.set(key, user);
      });

      setEmailList(new Map(updatedEmailList));
    }

    const processedEntity = processEntity(searchQuery);

    /**
     * currentCloudSiteHostname is populated only when handleTransferFormSubmit is called from partial search form.
     * For partial search, in both situation i.e. on existing cloudsite and on previously searched cloudsite,
     * we need to pass cloudsite url to fetch the external products.
     */
    const payload = {
      entity: currentCloudSiteHostname
        ? currentCloudSiteHostname
        : ((processedEntity.entitlementNumber || processedEntity.siteURL) as string),
      emails: Object.values(emailObj).map((email) => email.trim()),
      transactionAccountId: txaId,
    };

    /**
     * Remove the searched entitlement or cloudsite from the store, if it is already present.
     */
    actions.removeProductsAndApps({ ...processedEntity, removeOnlyTransferEntitlements: true });

    try {
      setEntitlementListWithEmailFetchStatus(EntitlementListWithEmailFetchStatus.PENDING);
      const productSubscriptions = await fetchEntitlementsForEntityWithContactEmails(payload);
      const selectedBillingProfile = billingProfilesForCustomer?.find((profile: any) => profile.id === invoiceGroupId);
      const productsAndAppData = getDataForStore({
        entitlementSubscriptionsWrapper: productSubscriptions,
        offeringsAndpricingPlans: offeringsAndPricingPlans,
        operationType: OperationType.TRANSFER,
        hasAllData: true,
        includeOrgProducts: true,
        showSandboxProducts,
        isSandboxDecouplingEnabled,
        isFutureDatedEntitlementEnabled,
        isMultiCurrencyEnabled,
        destinationCurrency: selectedBillingProfile?.currency || '',
        isNonCommercialPricingEnabled,
        destinationPricingEligibility: selectedBillingProfile?.shipToPriceEligibility || '',
        isTWCPhase1Enabled,
        isTWCPhase2Enabled,
        isAddOnsPhase2Enabled,
        isMultiInstanceAppsEnabled,
      });
      if (productsAndAppData) {
        actions.addProductsAndApps(productsAndAppData);
      }

      const hasUnprovisionedSubscription = !!productSubscriptions?.nodes?.find?.(
        (subscription) => !(subscription?.cloudSiteHostname || subscription?.orgId)
      );

      if (hasUnprovisionedSubscription) {
        const isEmptyResponse = isEmpty(productsAndAppData?.products);

        triggerErrorNotificationForTransfer(
          isEmptyResponse ? allEntitlementsAreUnprovisionedErrorMsg : someEntitlementIsUnprovisionedErrorMsg
        );

        // It is possible the complete response is empty. This happens when we search for an unprovisioned entitlement.
        // In this case, we should allow the user to search again.
        if (isEmptyResponse) return;
      }

      if (productsAndAppData) {
        const allProducts = Object.values(productsAndAppData.products);
        const cloudSiteIds = allProducts.map(({ cloudSiteId }) => cloudSiteId);
        if (cloudSiteIds && cloudSiteIds.length) {
          const externalProductListResponse = await fetchCloudSiteProductsBelongingToOtherTxa({
            currentTransactionAccountId: txaId,
            cloudSiteIds,
          });
          setTransferExternalProductsList(externalProductListResponse);
        }
      }
      setIsSubmittingTransferSearchForm(false);
      setShowTransferSearchResults(true);
      setEntitlementListWithEmailFetchStatus(EntitlementListWithEmailFetchStatus.SUCCESS);
      if (!notificationSent && !hasNoTransferSearchResults) {
        const notificationOptions = successNotificationBuilder()
          .withId('externally-managed-subscriptions-added')
          .withHeader('Externally-managed subscriptions added')
          .withMessage("They'll be transferred to your billing account when you confirm your order.")
          .withDismissAfter(5000)
          .isVersion(NotificationTypes.FLAG)
          .build();
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
      if (hasNoTransferSearchResults) {
        setHasNoTransferSearchResults(false);
      }
    } catch (e: any) {
      setEntitlementListWithEmailFetchStatus(EntitlementListWithEmailFetchStatus.ERROR);
      setIsSubmittingTransferSearchForm(false);
      triggerErrorNotificationForTransfer(e.message);
      setHasNoTransferSearchResults(true);
    }
  };

  const onInitialTransferSearchFormSubmit = async () => {
    const processedEntity = processEntity(searchQuery);
    // Need this state to track the submission state of form
    setIsSubmittingTransferSearchForm(true);
    /**
     * If entitlement number or cloudsite url searched for transfer is already present in the cart,
     * Need to open a modal to inform the user about removal of the searched entity from the cart.
     * then we should remove it from the store and add it again.
     */
    if (isSearchedEntitlementOrCloudsiteInCart(processedEntity)) {
      setShowClearingCartWarning(true);
    } else {
      const emailObj: Record<string, string> = {};
      emailList.forEach(({ email }, key) => {
        emailObj[key] = email;
      });
      handleTransferFormSubmit(emailObj);
    }
  };

  if (billingProfileError || fetchEntitlementsError) {
    triggerErrorNotification('Please try again or contact Partner Service Desk.', 'Could not fetch subscriptions');
  }

  /**
   * This function is used to get the last entry from the cloudSiteMapForTransfer.
   * @param cloudSiteMapForTransfer
   * @returns last entry from the cloudSiteMapForTransfer
   *
   * cloudSiteMapForTransfer has all the entry of searched cloudsite for transfer.
   * In order to show only the searched cloudsite, we need to get the last entry from the cloudSiteMapForTransfer.
   */
  const getValidEntry = (cloudSiteOrOrgNameMapForTransfer: CloudSiteOrOrgNameMap | null) => {
    if (!cloudSiteOrOrgNameMapForTransfer) return null;

    /**
     * On isSandboxDecouplingEnabled = true state, should not show the sandbox products just the last entry.
     * When showSandboxProducts = true and isSandboxDecouplingEnabled = false, then we need to show the sandbox products.
     */
    if (showSandboxProducts && !isSandboxDecouplingEnabled) {
      // Checking if searched query is an entitlement number or cloudsite url.
      const processedEntity = processEntity(searchQuery);
      const searchedEntitlementOrContainerId: string[] = [];
      if (processedEntity.entitlementNumber) {
        /**
         * If the searched query is an entitlement number,
         * then we need to get the containerId Or Id of the entitlement for which we need sandbox.
         */
        const searchedEntitlement = Object.values(products)?.find(
          (product) => product.entitlementNumber === searchQuery && product.operationType === OperationType.TRANSFER
        );

        const entitlementOrContainerId = searchedEntitlement?.containerId || searchedEntitlement?.id;
        if (entitlementOrContainerId) {
          searchedEntitlementOrContainerId.push(entitlementOrContainerId);
        }
      }

      if (processedEntity.siteURL) {
        /**
         * If the searched query is a cloudsite url,
         * then we need to get the containerId Or Id of all the products in cloudsite
         * for which we need sandbox.
         */
        const searchedCloudsiteProducts = Object.values(products)?.filter(
          (product) => product.cloudSite === searchQuery && product.operationType === OperationType.TRANSFER
        );

        searchedCloudsiteProducts.forEach((product) => {
          const entitlementOrContainerId = product?.containerId || product?.id;
          searchedEntitlementOrContainerId.push(entitlementOrContainerId);
        });
      }

      const validCloudsite: string[] = [];

      Object.keys(cloudSiteOrOrgNameMapForTransfer).forEach((cloudSiteHostname) => {
        /**
         * From the response cloudSiteOrOrgNameMapForTransfer, checking valid cloudsite
         */
        const isValidCloudsite = cloudSiteOrOrgNameMapForTransfer[cloudSiteHostname].products.some(
          (product) =>
            searchedEntitlementOrContainerId.includes(product.id) ||
            searchedEntitlementOrContainerId.includes(product.sandboxParent) ||
            searchedEntitlementOrContainerId.includes(product.containerId)
        );

        if (isValidCloudsite) {
          validCloudsite.push(cloudSiteHostname);
        }
      });

      if (validCloudsite.length !== 0) {
        return Object.fromEntries(
          Object.entries(cloudSiteOrOrgNameMapForTransfer).filter(([cloudSiteHostname]) =>
            validCloudsite.includes(cloudSiteHostname)
          )
        );
      }
    }

    const cloudSiteOrOrgNameMapForTransferEntries = Object.entries(cloudSiteOrOrgNameMapForTransfer);
    const cloudSiteOrOrgNameMapForTransferLength = cloudSiteOrOrgNameMapForTransferEntries.length;
    const lastEntry = cloudSiteOrOrgNameMapForTransferEntries[cloudSiteOrOrgNameMapForTransferLength - 1];

    return { [lastEntry[0]]: lastEntry[1] };
  };

  const showFreeTextContent = (): boolean => {
    return !!(
      !showTransferForm &&
      !showTransferSearchResults &&
      searchQuery &&
      !processEntity(searchQuery, true).isValid &&
      !cloudSiteOrOrgNameMap?.length
    );
  };

  const trimSearchQuery = (searchQuery: string) => {
    return processEntity(searchQuery, true).isValid ? searchQuery.trim() : searchQuery;
  };

  return (
    <section>
      {!!showCloudSiteContainer && (
        <SearchAndFilterContainer
          searchLineItems={billingProfiles}
          searchQuery={searchQuery}
          selectedLineItems={billingProfileIds}
          onQueryChange={(searchQuery: string) =>
            actions.setSidebarFilter({
              searchQuery: trimSearchQuery(searchQuery),
            })
          }
          onSelectedLineItemsChange={(billingProfileIds: string[]) => actions.setSidebarFilter({ billingProfileIds })}
          clearSearchQuery={clearSearchQuery}
          filterAddon={
            <Button
              appearance="default"
              onClick={() => actions.addAllToCart()}
              isDisabled={isEmpty(cloudSiteOrOrgNameMap)}
            >
              Add all
            </Button>
          }
          showTransferForm={!!showTransferForm}
          showTransferSearchResults={showTransferSearchResults}
          showFreeTextContent={showFreeTextContent()}
          onTransferSearchFormSubmit={onInitialTransferSearchFormSubmit}
          isLoading={isSubmittingTransferSearchForm}
          emailList={emailList}
          setEmailList={setEmailList}
          searchFieldPlaceholder={'Search by org name, site URL or E-#'}
        />
      )}
      <WarningDialog
        isOpen={isBillingProfileChangeConfirmOpen}
        onClose={() => setIsBillingProfileChangeConfirmOpen(false)}
        onConfirm={() => {
          setIsBillingProfileChangeConfirmOpen(false);
          onBillingProfileChange(billingProfileOption);
        }}
        header={'Your subscriptions will be cleared'}
        description={<> Leaving this page means the subscriptions you've added will be lost. </>}
        okLabel={'Continue'}
        testId={'billing-change-confirm-dialog'}
      />

      {hasNoTransferSearchResults && showTransferForm && <NoEntitlement />}

      {showTransferSearchResults && (
        <CloudSiteContainer
          cloudSiteOrOrgNameMap={getValidEntry(cloudSiteOrOrgNameMapForTransfer)}
          onChange={handleCheckedItems}
          isEntitlementListWithEmailLoading={isEntitlementListWithEmailLoading}
          externalProductsList={transferExternalProductsList}
          onTransferSearchFormSubmit={handleTransferFormSubmit}
          isRenderingTransferFlowEntitlements
          showInactiveProducts={showInactiveProducts}
          shouldShowAddons={shouldShowAddons}
          shouldShowSandboxStatus={showSandboxProducts || isSandboxDecouplingEnabled}
          isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
        />
      )}

      {externalCloudSiteOrOrgNameMap && !showTransferForm && (
        <CloudSiteContainer
          cloudSiteOrOrgNameMap={externalCloudSiteOrOrgNameMap}
          onChange={handleCheckedItems}
          isRenderingExternalCloudSiteEntitlements
          showInactiveProducts={showInactiveProducts}
          shouldShowAddons={shouldShowAddons}
          shouldShowSandboxStatus={showSandboxProducts || isSandboxDecouplingEnabled}
          isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
        />
      )}

      {showCloudSiteContainer && !showTransferForm && !showTransferSearchResults ? (
        <CloudSiteContainer
          billingProfiles={billingProfilesFromStore}
          cloudSiteOrOrgNameMap={null}
          productsGroupedByBillingProfileAndCloudSite={
            cloudSiteOrOrgNameMap as CloudSiteOrOrgNameMapGroupedByBillingProfile
          }
          isExternalProductListLoading={isExternalProductListLoading}
          externalProductsList={externalProductsList}
          onChange={handleCheckedItems}
          showNoEntitlement={!!billProfileId && !isCloudSiteContainerLoading}
          isLoading={isCloudSiteContainerLoading}
          showErrorState={!!billingProfileError || !!fetchEntitlementsError}
          handleRefetchEntitlements={handleRefetchEntitlements}
          isSelectionDisabled={false}
          isBillingProfileLoading={isBillingProfileLoading}
          isFetchEntitlementsLoading={isFetchEntitlementsLoading}
          showTransferForm={!!showTransferForm}
          onTransferSearchFormSubmit={handleTransferFormSubmit}
          showInactiveProducts={showInactiveProducts}
          shouldShowAddons={shouldShowAddons}
          shouldShowSandboxStatus={showSandboxProducts || isSandboxDecouplingEnabled}
          isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
        />
      ) : null}
      <WarningDialog
        isOpen={showClearingCartWarning}
        onClose={() => {
          setIsSubmittingTransferSearchForm(false);
          setShowClearingCartWarning(false);
        }}
        onConfirm={() => {
          setShowClearingCartWarning(false);
          const emailObj: Record<string, string> = {};
          emailList.forEach(({ email }, key) => {
            emailObj[key] = email;
          });
          handleTransferFormSubmit(emailObj);
        }}
        header={'This action will remove searched cloudsite from cart'}
        description={<> On submitting, the searched cloudsite will be removed from the cart. </>}
        okLabel={'Continue'}
      />
    </section>
  );
};
